import Vue from 'vue'
import store from '@/store'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { normalize } from 'normalizr'
import { scheduleSchema } from '@/schema/schedule'
import { scheduleCommentSchema } from '@/schema/scheduleComment'
import { ScheduleEntities } from '@/schema/schedule/types'
import { Schedule } from '@/types/schedule'
import { ScheduleListState } from '@/store/schedule/types'
import {
  ScheduleCommentState,
  ScheduleCommentListState,
  ScheduleCommentRelation
} from '@/store/scheduleComment/types'
import { ScheduleListResponse } from '@/api/schedule/types'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  scheduleCommentRelation: {},
  comments: {},
  nextUrl: null,
  prevUrl: null
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  scheduleCommentRelation: state => state.scheduleCommentRelation,
  comments: state => state.comments,
  hasNext: state => state.nextUrl !== null,
  hasPrev: state => state.prevUrl !== null
}

/* -------------------------------------------------------------------------- */

const mergeScheduleCommnetRelation = (
  current: ScheduleCommentRelation,
  additinal: ScheduleCommentRelation,
  toAppend: boolean
): ScheduleCommentRelation => {
  const added = Object.keys(additinal).reduce(
    (marged, id) => ({
      ...marged,
      [id]: toAppend
        ? (current[Number(id)] || []).concat(additinal[Number(id)])
        : additinal[Number(id)].concat(current[Number(id)] || [])
    }),
    {} as ScheduleCommentRelation
  )
  return Object.assign({}, current, added)
}
const mutations: Mutations<S, M> = {
  clear: state => {
    state.comments = {}
    state.scheduleCommentRelation = {}
    state.nextUrl = null
    state.prevUrl = null
  },

  add: (state, { comments }) => {
    state.comments = Object.assign({}, state.comments, comments)
  },

  addScheduleCommentRelation: (state, { relation, toAppend = true }) => {
    state.scheduleCommentRelation = mergeScheduleCommnetRelation(
      state.scheduleCommentRelation,
      relation,
      toAppend
    )
  },

  insertScheduleCommentRelation: (state, { scheduleId, commentId, index }) => {
    state.scheduleCommentRelation[scheduleId].splice(index, 0, commentId)
  },

  setPagenationUrl: (state, { next, previous }) => {
    if (next) state.nextUrl = next
    if (previous) state.prevUrl = previous
  },

  deleteScheduleComment: (state, { commentId }) => {
    Object.keys(state.comments)
      .filter(
        id => state.comments[Number(id)].parentComment === Number(commentId)
      )
      .map(Number)
      .concat([commentId])
      .forEach(commentId => {
        const comment = state.comments[commentId]

        if (!comment) {
          return
        }

        const index = state.scheduleCommentRelation[comment.event].indexOf(
          comment.id
        )

        if (index > -1) {
          state.scheduleCommentRelation[comment.event].splice(index, 1)
        }

        Vue.delete(state.comments, commentId)
      })
  }
}

/* -------------------------------------------------------------------------- */

const buildScheduleCommentRelation = (
  schedules: ScheduleListState
): ScheduleCommentRelation =>
  Object.keys(schedules).reduce(
    (relation, id) => ({
      ...relation,
      [id]: schedules[Number(id)].comments || []
    }),
    {} as ScheduleCommentRelation
  )

const updateScheduleComment = (
  response: ScheduleListResponse,
  toAppend?: boolean
) => {
  const { result, entities } = normalize<
    Schedule[],
    ScheduleEntities,
    number[]
  >(response.results, [scheduleSchema])

  const { patients, scheduleComments, schedules, workers } = entities

  const { next, previous, count } = response
  const comments = (!scheduleComments
    ? {}
    : scheduleComments) as ScheduleCommentListState

  const relation = buildScheduleCommentRelation(schedules)

  store.commit('scheduleComment/addScheduleCommentRelation', {
    relation,
    toAppend
  })
  const pagination = {
    next: toAppend !== false ? next : null,
    previous: toAppend !== true ? previous : null
  }
  store.commit('scheduleComment/setPagenationUrl', pagination)
  store.commit('scheduleComment/add', { comments })

  store.commit('schedule/add', {
    schedules,
    toAppend,
    sort: result,
    totalCount: count
  })

  store.commit('worker/add', { workers })
  store.commit('patient/add', { patients })
}

const findIndexToInsertComment = (
  newComment: ScheduleCommentState,
  sort: Array<number>,
  currentComments: ScheduleCommentListState
) => {
  if (newComment.parentComment === null) {
    return sort.length
  }
  const parentIndex = sort.indexOf(newComment.parentComment as number)
  const nextParentCommentIndex = sort.findIndex(
    (id, index) =>
      index > parentIndex && currentComments[id].parentComment == null
  )
  return nextParentCommentIndex === -1 ? sort.length : nextParentCommentIndex
}

const actions: Actions<S, A, G, M> = {
  async load(context, { patientId, scheduleId }) {
    const response = await Vue.$api.scheduleComment.fetch({
      patientId,
      scheduleId
    })
    updateScheduleComment(response)
  },

  async paginateNext({ state, getters }) {
    if (!getters.hasNext) {
      return
    }
    const response = await Vue.$api.scheduleComment.fetchPage({
      url: (state.nextUrl as string).replace(/^https?:\/\/.*?api\//, '')
    })
    updateScheduleComment(response, true)
  },

  async paginatePrev({ state, getters }) {
    if (!getters.hasPrev) {
      return
    }
    const response = await Vue.$api.scheduleComment.fetchPage({
      url: (state.prevUrl as string).replace(/^https?:\/\/.*?api\//, '')
    })
    updateScheduleComment(response, false)
  },

  async create({ commit, getters }, params) {
    const response = await Vue.$api.scheduleComment.create({ params })

    const { result, entities } = normalize<Schedule, ScheduleEntities, number>(
      response,
      scheduleCommentSchema
    )
    const comments = entities.scheduleComments as ScheduleCommentListState
    const newComment = comments[result]

    const index = findIndexToInsertComment(
      newComment,
      getters['scheduleCommentRelation'][newComment.event],
      getters['comments']
    )

    commit('add', { comments })

    commit('insertScheduleCommentRelation', {
      scheduleId: newComment.event,
      commentId: result,
      index
    })
  },

  async deleteScheduleComment({ commit }, { commentId }) {
    await Vue.$api.scheduleComment.delete({ commentId })
    commit('deleteScheduleComment', { commentId })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
